// userContext.js
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";
import { obtenerEstudiantePorId } from "../services/EstudianteService";
import { obtenerPersonalBibliotecarioPorCorreo } from "../services/PersonalBibliotecarioService";
import { refreshAccess, logout } from "../services/AuthService";
import { getAccessToken, clearAccessToken } from "../services/authTokenMemory";
import { jwtDecode } from "jwt-decode";
import { enviarFeedback } from "../services/FeedBackService"; // <-- importa el servicio

const UserContext = createContext();
export const useUser = () => useContext(UserContext);

export const UserProvider = ({ children }) => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const logoutInProgress = useRef(null);

  const updateUserData = (patch) => {
    setUserData((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  const fetchUserData = async () => {
    const t = getAccessToken();
    if (!t) {
      setUserData(null);
      return;
    }
    let decoded = null;
    try {
      decoded = jwtDecode(t); // { sub, role, exp, ... }
    } catch {
      setUserData(null);
      return;
    }

    try {
      const isEmail = decoded.sub?.includes("@");
      const resp = !isEmail
        ? await obtenerEstudiantePorId(decoded.sub)
        : await obtenerPersonalBibliotecarioPorCorreo(decoded.sub);

      setUserData({ ...resp.data, authRole: decoded.role, role: decoded.role === "user" ? "user" : "biblioteca" });
    } catch (e) {
      await Swal.fire({
        title: "Su sesión ha expirado, por favor vuelva a iniciar sesión",
        icon: "error",
        confirmButtonText: "Aceptar",
        confirmButtonColor: "#21807C",
      });
      await doLogout(); // asegúrate de limpiar
    }
  };

  const doLogout = async () => {
    try {
      await logout();
    } catch (error) {
      await Swal.fire({
        title: "No se confirmó el cierre de sesión",
        text: "Compruebe su conexión y vuelva a pulsar Cerrar sesión. El tiempo registrado queda limitado por el vencimiento de la sesión.",
        icon: "error",
        confirmButtonText: "Aceptar",
        confirmButtonColor: "#21807C",
      });
      throw error;
    }
    clearAccessToken();
    setUserData(null);
  };

  // Nuevo: logout con encuesta solo para rol "user"
  const logoutWithFeedback = async () => {
    // Si no hay userData todavía, o el rol NO es "user", cierra sesión directo
    if (!userData || userData.role !== "user") {
      await doLogout();
      return;
    }

    // 1) Calificación 1-5
    const { value: calificacion } = await Swal.fire({
      title: "Antes de irte...",
      icon: "question",
      input: "range",
      inputLabel: "Califica la plataforma",
      inputAttributes: { min: 1, max: 5, step: 1 },
      inputValue: 3,
      confirmButtonColor: "#21807C",
      showCancelButton: true,
      confirmButtonText: "Siguiente",
      cancelButtonText: "Ahora no",
      customClass: { title: "custom-title" },
    });

    // Si cancela el primer modal -> cerrar sesión sin feedback
    if (calificacion === undefined) {
      await doLogout();
      return;
    }

    // 2) Comentario
    const { value: comentario } = await Swal.fire({
      title: "¿Encontraste el libro que buscabas? ¿Qué te parece la plataforma?",
      icon: "question",
      input: "textarea",
      inputPlaceholder: "Escribe tu comentario aquí...",
      inputAttributes: { maxlength: 1000 },
      confirmButtonColor: "#21807C",
      showCancelButton: true,
      confirmButtonText: "Enviar",
      cancelButtonText: "Cancelar",
      customClass: { title: "custom-title" },
    });

    // Si cancela el comentario -> cerrar sesión de todas formas (sin feedback)
    if (comentario === undefined) {
      await doLogout();
      return;
    }

    // 3) Enviar feedback (antes de cerrar sesión para mantener Authorization)
    try {
      await enviarFeedback({
        calificacion: parseInt(calificacion),
        comentario: comentario,
      });
      await Swal.fire({
        title: "¡Gracias por tu opinión!",
        text: "Nos ayudas a mejorar",
        icon: "success",
        confirmButtonColor: "#21807C",
        customClass: { title: "custom-title" },
      });
    } catch (error) {
      // Si falla, muestra error pero prosigue con el logout
      await Swal.fire({
        title: "Error",
        text: "No se pudo enviar tu feedback.",
        icon: "error",
        confirmButtonColor: "#21807C",
      });
    } finally {
      await doLogout();
    }
  };

  const logoutUser = () => {
    if (!logoutInProgress.current) {
      logoutInProgress.current = logoutWithFeedback().finally(() => { logoutInProgress.current = null; });
    }
    return logoutInProgress.current;
  };

  // Al iniciar la app: intenta refrescar para poblar el access token en memoria
  useEffect(() => {
    (async () => {
      try {
        if (!getAccessToken()) {
          await refreshAccess(); // si hay cookie HttpOnly válida, obtendrás el access
        }
      } catch {
        // no hay cookie o es inválida
      } finally {
        try {
          await fetchUserData();
        } catch {
          // fetchUserData/doLogout already show an actionable error.
        } finally {
          setLoading(false);
        }
      }
    })();

  }, []);

  return (
    <UserContext.Provider
      value={{
        userData,
        loading,
        fetchUserData,
        updateUserData,
        logoutUser,     // <-- expone la versión con encuesta condicional
        setUserData,    // opcional, por si lo necesitás desde otros componentes
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
